import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { consultarCategorias } from '../../../nucleo/api/nucleoApi.js';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { consultarCuentas, PROPOSITOS } from '../../cuentas/api/cuentasApi.js';
import { editarMovimiento, obtenerMovimiento, registrarMovimiento, TIPOS } from '../api/movimientosApi.js';
import { CamposDeCaptura, SaldosAfectados } from '../componentes/CamposDeCaptura.jsx';
import { AYUDA_DE_DEVOLUCION } from '../formato.js';
import { useContextoDeRegreso } from '../../../nucleo/navegacion/useContextoDeRegreso.js';
import { conCapturaLimpia, useResumenCedeElPasoAlMenu } from '../navegacion/capturaLimpia.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';

/**
 * Captura de GASTO, INGRESO y DEVOLUCIÓN (RF-048, RF-049, RF-063) y edición de
 * cualquiera de ellos (RF-051).
 *
 * Es UNA pantalla con tres caras porque son cinco campos y ninguno más; lo que
 * cambia es el catálogo de categorías, los textos y si se ofrece la casilla de
 * meses sin intereses. Lo que NO cambia nunca:
 *   · La cuenta ofrecida es la del propósito CAPTURAR_ORIGEN: propia y activa
 *     (RN-067a). En el INGRESO, además, sin tarjetas de crédito (RN-077c).
 *   · LA CUENTA LLEGA VACÍA Y HAY QUE ELEGIRLA (RF-153, D-247, RN-433).
 *     [CAMBIADO 2026-10-03 · incremento 09] Antes venía preseleccionada con la
 *     última usada en este dispositivo (D-43, derogada). Motivo registrado, y es
 *     un error YA OCURRIDO en uso real: un movimiento quedó registrado en una
 *     cuenta distinta de la querida por no revisar lo que venía puesto. La
 *     preferencia por dispositivo se retiró completa, módulo incluido (RN-434).
 *     ÚNICA EXCEPCIÓN, cerrada (D-255, RN-444): si la dirección trae la cuenta
 *     porque un botón que la nombra la escribió ahí —«Registrar devolución» desde
 *     el detalle de un gasto—, esa cuenta SE CONSERVA. No es preselección: es lo
 *     que la persona pidió con el dedo.
 *   · La operación termina en UN SOLO ENVÍO: no hay pantalla intermedia de
 *     confirmación (RF-048).
 *   · AL EDITAR NO SE PUEDE CAMBIAR EL TIPO (D-35): la pantalla ni lo ofrece, y el
 *     servidor responde TIPO_MOVIMIENTO_NO_MODIFICABLE si se fuerza.
 *
 * LO QUE EL FIX DE INGRESOS CAMBIÓ AQUÍ (punto 1): el «+» desde el resumen de
 * guardado abre esta pantalla LIMPIA, sin recargar (D-479). No se reinicia campo
 * por campo: la pantalla se monta de nuevo en cada paso de navegación, y eso lo
 * resuelve `navegacion/capturaLimpia.jsx`, compartido con el traspaso. Esta
 * pantalla solo declara cuándo está mostrando el resumen.
 */
function CapturaDeMovimiento({ tipo }) {
  const { idMovimiento } = useParams();
  const [parametros] = useSearchParams();

  const esEdicion = Boolean(idMovimiento);
  const hoy = obtenerFechaDeNegocioHoy();

  /*
   * Al terminar —guardar, cancelar o tocar la flecha— se vuelve a la DIRECCIÓN
   * EXACTA desde la que se abrió el alta, con su pestaña y sus filtros tal como
   * estaban (RF-146, RN-390), y se vuelve REEMPLAZANDO el paso del formulario
   * (RF-148). Sin contexto de origen se vuelve al destino padre de siempre, que
   * es la conducta de hoy (RN-390c).
   */
  const { destinoDeRegreso } = useContextoDeRegreso('/movimientos');

  const [cuentas, setCuentas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  /*
   * RF-187 · ARRANCA EN VERDADERO, AQUÍ MISMO, EN EL `useState` INICIAL, y no al
   * empezar la petición: si se pusiera a verdadero dentro de `cargar`, el PRIMER
   * fotograma seguiría sin campo de cuenta y el defecto seguiría vivo. La lista
   * sigue siendo `[]`, nunca nula (RN-566).
   */
  const [cargandoCuentas, setCargandoCuentas] = useState(true);
  /*
   * RF-187 / RN-655 · Desde el 17 la categoría también se elige con el control
   * único, así que su catálogo también declara su estado de carga. Arranca en
   * verdadero en el `useState` inicial, por la misma razón que el de cuentas.
   */
  const [cargandoCategorias, setCargandoCategorias] = useState(true);
  /*
   * T-17-02 · H-20 (RF-235, RN-659 a RN-661, D-359). DOS INDICADORES PARA UNA SOLA
   * COSA: que el foco automático del importe NO SE ADELANTE AL DATO.
   *
   * `cargandoMovimiento` vale verdadero mientras la edición no ha recibido el
   * movimiento —y vale FALSO de entrada en el alta, donde no hay dato que esperar
   * y el foco sigue siendo inmediato (D-229, conducta intacta)—.
   *
   * `tecleadoAntesDelDato` recuerda que la persona ESCRIBIÓ en el importe antes de
   * que el movimiento llegara. Si eso pasó, LO TECLEADO MANDA (RN-661): ni el
   * valor guardado lo pisa, ni se le quita el foco para «marcar» nada.
   *
   * Es una REFERENCIA y no estado a propósito: el único momento en que se consulta
   * es cuando llega el movimiento, y guardarlo en estado volvería a disparar la
   * carga (va dentro de `cargar`) o repintaría la pantalla en cada pulsación sin
   * que nada de lo pintado dependa de él todavía.
   */
  const [cargandoMovimiento, setCargandoMovimiento] = useState(esEdicion);
  const tecleadoAntesDelDato = useRef(false);
  const [idCuenta, setIdCuenta] = useState(null);
  const [idCategoria, setIdCategoria] = useState(null);
  const [importe, setImporte] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [nota, setNota] = useState('');
  const [mesesSinIntereses, setMesesSinIntereses] = useState(false);
  const [version, setVersion] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [faltaCuenta, setFaltaCuenta] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const textos = useMemo(() => TEXTOS[tipo], [tipo]);

  /*
   * RN-FIX-M01(b) · con el resumen a la vista, el menú del «+» hereda de él el
   * destino de regreso, para que la captura limpia vuelva a la MISMA lista con el
   * MISMO filtro (CA-FIX-M01 paso 4).
   */
  useResumenCedeElPasoAlMenu(destinoDeRegreso, Boolean(resultado));

  const cargar = useCallback(async () => {
    try {
      setError(null);

      // El INGRESO no se captura contra una tarjeta de crédito: lo que entra a una
      // tarjeta es un pago o una devolución (D-41, RN-077c). El filtro es ayuda
      // visual; el servidor lo rechaza igual si se fuerza.
      const disponibles = await consultarCuentas({
        proposito: PROPOSITOS.capturarOrigen,
        clase: tipo === TIPOS.ingreso ? 'NO_CREDITO' : 'TODAS',
      });

      const catalogo = await consultarCategorias({
        // En una DEVOLUCIÓN se piden las de tipo GASTO: se devuelve lo que se
        // gastó, y por eso resta del gasto de esa categoría (D-47, RN-093b).
        tipo: tipo === TIPOS.ingreso ? 'INGRESO' : 'GASTO',
        soloActivas: true,
      });

      setCuentas(disponibles.cuentas);
      setCategorias(catalogo);

      if (esEdicion) {
        const detalle = await obtenerMovimiento(idMovimiento);
        const movimiento = detalle.movimiento;
        setIdCuenta(movimiento.idCuenta);
        setIdCategoria(movimiento.idCategoria);
        /*
         * RN-661 · EL VALOR GUARDADO NO PISA JAMÁS LO QUE LA PERSONA ESCRIBIÓ. La
         * precarga del importe —que ya existía y nunca fue el problema— se adopta
         * solo si nadie se adelantó a teclear. Si el campo quedó enfocado pero
         * INTACTO, aquí entra el valor guardado y el remontaje del campo lo deja
         * marcado completo, que es el estado que el foco prometía.
         */
        if (!tecleadoAntesDelDato.current) setImporte(movimiento.importe);
        setFecha(movimiento.fecha);
        setNota(movimiento.nota ?? '');
        setMesesSinIntereses(movimiento.esMesesSinIntereses);
        setVersion(movimiento.version);
        return;
      }

      // [RETIRADO 2026-10-03 · RF-153, D-247, RN-433a] Aquí se sembraba la cuenta
      // con `elegirCuentaInicial(...)`, la preselección POR DISPOSITIVO de D-43.
      // El campo arranca VACÍO y la persona tiene que elegirlo.

      // "Registrar devolución" desde el detalle del gasto copia su categoría: es el
      // camino que evita capturar un reembolso como ingreso (D-50, RN-096c).
      //
      // RN-388: nunca se sugiere una categoría que este formulario rechazaría —por
      // ejemplo una de ingreso en un gasto—. Si no es ofrecible, SE DESCARTA EN
      // SILENCIO: ni aviso ni error.
      const categoriaSugerida = Number(parametros.get('idCategoria'));
      if (categoriaSugerida && catalogo.some((c) => c.idCategoria === categoriaSugerida)) {
        setIdCategoria(categoriaSugerida);
      }

      // LA EXCEPCIÓN DE D-255, Y NO SE TOCA (RN-444, RN-445, RN-446b, CR-09a).
      // Esta cuenta NO la sembró el sistema: la escribió en la dirección el botón
      // «Registrar devolución», que nombra la cuenta del gasto original. Estaba
      // seis renglones debajo de la siembra que se retiró y escribe el MISMO
      // estado: retirar las dos juntas —o «limpiar esta por consistencia»— rompe
      // la devolución. Se descarta en silencio si no es una cuenta ofrecible,
      // mismo criterio que la categoría.
      const cuentaSugerida = Number(parametros.get('idCuenta'));
      if (cuentaSugerida && disponibles.cuentas.some((c) => c.idCuenta === cuentaSugerida)) {
        setIdCuenta(cuentaSugerida);
      }
    } catch (fallo) {
      setError(fallo);
    } finally {
      /*
       * RN-567 · VUELVE A FALSO ACIERTE O FALLE LA CARGA, y también cuando la rama
       * de edición sale con `return` antes del final del `try`: de ahí el `finally`.
       * Si falla, la lista queda vacía y manda RN-552 —el campo no se pinta y el
       * aviso de error es de esta pantalla—. Un campo deshabilitado para siempre es
       * peor que no tener campo.
       */
      setCargandoCuentas(false);
      setCargandoCategorias(false);
      /*
       * RN-661 · ACIERTE O FALLE LA CARGA. Si el movimiento no llegó, el campo
       * queda vacío y el error es el de la pantalla, como hoy: lo que no puede
       * pasar es que el campo se quede esperando un dato que ya no va a venir.
       */
      setCargandoMovimiento(false);
    }
  }, [tipo, esEdicion, idMovimiento, parametros]);

  useEffect(() => { cargar(); }, [cargar]);

  /*
   * T-17-02 · RN-661 · QUÉ CUENTA COMO «LA PERSONA TECLEÓ» MIENTRAS EL MOVIMIENTO
   * VIAJA. Un texto NO VACÍO escrito en el campo manda sobre el valor guardado.
   *
   * UN VACÍO NO CUENTA, Y ESO ES LA MITAD DEL DEFECTO H-20: el campo de importe
   * avisa `''` al salir cuando estaba intacto (su `onBlur` normaliza), y era
   * justamente ese vacío el que pisaba el importe precargado. Tomarlo por «tecleo»
   * dejaría el campo vacío para un importe que existe, que es lo que RN-659
   * prohíbe.
   */
  const escribirImporte = useCallback((nuevo) => {
    if (cargandoMovimiento && String(nuevo ?? '').trim() !== '') {
      tecleadoAntesDelDato.current = true;
    }
    setImporte(nuevo);
  }, [cargandoMovimiento]);

  /*
   * ¿ESTE MONTAJE DEL CAMPO DE IMPORTE SE LLEVA EL FOCO? (RF-236, RN-660, RN-661)
   *   · En el ALTA: siempre. `esEdicion` es falso, `cargandoMovimiento` arranca en
   *     falso y esto vale verdadero desde el primer fotograma: el campo se comporta
   *     EXACTAMENTE como antes del 17 (CA-17-08).
   *   · En la EDICIÓN: falso mientras el movimiento viaja —el foco espera al dato—
   *     y verdadero en cuanto el importe guardado está presentado.
   *   · Si la persona se adelantó a teclear, se queda en falso: no se le arrebata
   *     el foco ni se le marca el campo por encima de lo que escribió.
   */
  const tomarElFocoDelImporte = !cargandoMovimiento && !tecleadoAntesDelDato.current;

  async function guardar(evento) {
    evento.preventDefault();
    setError(null);

    /*
     * RN-436. Sin cuenta elegida EL FORMULARIO NO ENVÍA, y el aviso usa la pieza
     * de aviso de error del núcleo, sin inventar un estilo nuevo. Es la otra mitad
     * de RF-153: el campo llega vacío Y OBLIGA A ELEGIR.
     */
    if (!idCuenta) {
      setError(null);
      setFaltaCuenta(true);
      return;
    }
    setFaltaCuenta(false);
    setGuardando(true);

    try {
      const peticion = {
        tipo,
        idCuenta,
        idCategoria,
        importe,
        fecha,
        // FECHA LOCAL DEL DISPOSITIVO: es contra ella —y no contra el reloj del
        // servidor— como se decide si la fecha es futura (D-45, RN-075d).
        fechaLocal: hoy,
        esMesesSinIntereses: mesesSinIntereses,
        nota: nota === '' ? null : nota,
        version,
      };

      const respuesta = await guardarConConexion(() => (esEdicion
        ? editarMovimiento(idMovimiento, peticion)
        : registrarMovimiento(peticion)));

      // [RETIRADO 2026-10-03 · RF-153, D-247, RN-434] Aquí se guardaba la cuenta
      // usada en el navegador para preseleccionarla la próxima vez. Al no haber
      // quien la lea, la escritura se retiró también: un módulo que guarda la
      // última cuenta usada y no la usa es la invitación perfecta a que alguien lo
      // reconecte dentro de tres incrementos y reabra el error que D-247 corrige.
      setResultado(respuesta);
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  if (resultado) {
    /*
     * RN-394. El resumen vive en la MISMA ruta que el formulario, así que no hay
     * un paso que reemplazar: lo que hacía caer de vuelta en el formulario era la
     * flecha de volver, que EMPUJABA un paso nuevo (CR-02a). Ahora la flecha del
     * armazón reemplaza el paso, y «Ver el historial» también: al salir del
     * resumen el formulario deja de ser alcanzable con el botón atrás.
     * No se añade ninguna pantalla nueva ni confirmación intermedia: RF-048 manda
     * que la captura termine en un solo envío.
     */
    return (
      <Shell titulo={textos.tituloGuardado} volverA={destinoDeRegreso}>
        {/* Fila 27 del inventario del incremento 15 (RF-222/RF-223, D-327, RN-628): salió
            el identificador interno «· #{id}» —no es un folio visible— y la justificación
            del diseño («sin pantalla intermedia de confirmación»). */}
        <div className="aviso ok" data-prueba="aviso-guardado">
          <b>{textos.tituloGuardado}.</b> Ya está en tu historial y en los totales del mes.
        </div>

        <SaldosAfectados resultado={resultado} />

        {/* AVISO DECIDIDO POR EL SERVIDOR con la clave de siembra de la categoría,
            no por el nombre ni por una comparación de la pantalla (D-50, RN-096). */}
        {resultado.avisoReembolso && (
          <div className="aviso w">
            <b>Lo guardaste como ingreso con la categoría “Reembolsos”. Está aceptado</b>: si
            ese dinero <b>no</b> corresponde a un gasto capturado aquí, es exactamente lo correcto.
            <p>
              <b>¿Era la devolución de una compra que sí está registrada?</b> Entonces el mes queda con
              el gasto inflado y el ingreso inflado a la vez. Se arregla sin perder nada:
              cancela o borra este ingreso y captura la devolución desde el detalle del gasto original.
            </p>
            <Link className="btn sec" to={`/movimientos/${resultado.movimiento.idMovimiento}`}>
              Ver este ingreso y corregirlo
            </Link>{' '}
            <Link className="btn sec" to="/movimientos/devolucion">Registrar la devolución</Link>
          </div>
        )}

        {/* Vuelve AL MISMO FILTRO del que se vino, reemplazando el paso (RF-146,
            RF-148, RN-437). Consecuencia asumida a propósito: si el movimiento
            recién guardado no cae dentro de ese filtro, NO APARECE en la lista, y
            NO se avisa nada —la confirmación de guardado de arriba es la única
            (D-246, RN-438)—. */}
        <div style={{ margin: '10px 12px' }}>
          <Link className="btn ancho" to={destinoDeRegreso} replace>Ver el historial</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell titulo={textos.titulo} subtitulo={textos.subtitulo} volverA={destinoDeRegreso}>
      <AvisoDeError error={error} />

      {/* RN-436: el aviso de la cuenta sin elegir usa la pieza del núcleo, sin
          estilo nuevo, y dice qué pasó y qué hacer. */}
      {faltaCuenta && (
        <AvisoDeError
          mensaje="Falta elegir la cuenta. Elige una cuenta o tarjeta y vuelve a guardar."
          datoDePrueba="falta-la-cuenta"
        />
      )}

      {/* CA-FIX-M01 · marca estable del formulario, para poder afirmar que llegó la
          captura limpia y que el aviso de guardado NO está (`aviso-guardado`). */}
      <form onSubmit={guardar} data-prueba="formulario-de-captura">
        <CamposDeCaptura
          etiquetaDeCategoria={textos.etiquetaDeCategoria}
          categorias={categorias}
          idCategoria={idCategoria}
          alElegirCategoria={setIdCategoria}
          etiquetaDeCuenta={textos.etiquetaDeCuenta}
          cuentas={cuentas}
          idCuenta={idCuenta}
          alElegirCuenta={setIdCuenta}
          /* RF-187 · El campo de cuenta está en pantalla desde el primer fotograma
             (RN-562 a RN-565, CA-11-37). */
          cargandoCuentas={cargandoCuentas}
          /* RF-187 / RN-655 · El catálogo de categorías declara su estado de carga
             desde que la categoría se elige con el control único (T-17-01). */
          cargandoCategorias={cargandoCategorias}
          importe={importe}
          alEscribirImporte={escribirImporte}
          /* T-17-02 · H-20 · el foco del importe no se adelanta al dato (RN-660). */
          tomarElFocoDelImporte={tomarElFocoDelImporte}
          fecha={fecha}
          alElegirFecha={setFecha}
          nota={nota}
          alEscribirNota={setNota}
          ofreceMesesSinIntereses={tipo === TIPOS.gasto}
          mesesSinIntereses={mesesSinIntereses}
          alMarcarMesesSinIntereses={setMesesSinIntereses}
          ayuda={textos.ayuda}
        />

        <div style={{ margin: '10px 12px' }}>
          <button className="btn ancho" type="submit" disabled={guardando}>
            {esEdicion ? 'Guardar cambios' : textos.botonGuardar}
          </button>
          {/* Cancelar también vuelve al contexto de origen y REEMPLAZA el paso
              del formulario (RF-146, RF-148). */}
          <Link className="btn sec ancho" to={destinoDeRegreso} replace>Cancelar</Link>
        </div>
      </form>
    </Shell>
  );
}

/*
 * D-479 · la pantalla se monta limpia en cada paso de navegación. La envoltura es
 * la MISMA pieza que envuelve al traspaso: la corrección se escribe una vez.
 */
export default conCapturaLimpia(CapturaDeMovimiento);

const TEXTOS = {
  [TIPOS.gasto]: {
    titulo: 'Nuevo gasto',
    subtitulo: 'Cinco campos y ninguno más',
    tituloGuardado: 'Gasto guardado',
    botonGuardar: 'Guardar gasto',
    etiquetaDeCategoria: 'CATEGORÍA — obligatoria en un gasto',
    etiquetaDeCuenta: 'CUENTA O TARJETA — de dónde sale el dinero',
    ayuda: (
      <div className="aviso i">
        Un gasto <b>sí puede ir contra una tarjeta de crédito</b>: aumenta la deuda y{' '}
        <b>no baja ningún saldo de banco</b>.
      </div>
    ),
  },
  [TIPOS.ingreso]: {
    titulo: 'Nuevo ingreso',
    subtitulo: 'Mismo formulario que el gasto',
    tituloGuardado: 'Ingreso guardado',
    botonGuardar: 'Guardar ingreso',
    etiquetaDeCategoria: 'CATEGORÍA DE INGRESO — obligatoria',
    etiquetaDeCuenta: 'CUENTA QUE RECIBE EL DINERO',
    // TEXTO FIJO Y SIEMPRE VISIBLE, elijas la categoría que elijas: la pantalla NO
    // inspecciona cuál se marcó (RN-096d). El aviso por "Reembolsos" lo decide el
    // servidor DESPUÉS de guardar, con la clave de siembra.
    ayuda: (
      <div className="aviso s" data-prueba="ayuda-devolucion">
        <b>{AYUDA_DE_DEVOLUCION}</b>
        {/* Fila 29 del inventario del incremento 15 (RF-222): salió el párrafo secundario,
            que hablaba de la propia pantalla en vez de hablarle a la persona. El texto
            principal de la ayuda de devolución se conserva intacto y el recuadro no se mueve. */}
        <Link className="btn sec" to="/movimientos/devolucion">
          Registrar devolución en vez de ingreso
        </Link>
      </div>
    ),
  },
  [TIPOS.devolucion]: {
    titulo: 'Registrar devolución',
    subtitulo: 'Es gasto que se deshace, no un ingreso',
    tituloGuardado: 'Devolución guardada',
    botonGuardar: 'Guardar devolución',
    etiquetaDeCategoria: 'CATEGORÍA — de GASTO, la misma de la compra que devuelves',
    etiquetaDeCuenta: 'CUENTA O TARJETA QUE RECIBE EL DINERO — tuya y activa',
    ayuda: (
      <div className="aviso s">
        <b>Una devolución no es una cancelación.</b> Crea un <b>movimiento nuevo</b> y el gasto original
        <b> se conserva intacto</b>: es dinero que se movió de verdad. En una tarjeta baja la
        deuda; en una cuenta sube el saldo, y <b>resta del gasto de su categoría sin sumar jamás en
        ingresos</b>.
        <div className="ayuda">
          <b>Y tampoco es un ingreso con la categoría “Reembolsos”</b>: esa es para dinero que{' '}
          <b>no</b> corresponde a un gasto capturado aquí. Una devolución{' '}
          <b>no admite meses sin intereses</b>, y por eso la casilla ni se muestra.
        </div>
      </div>
    ),
  },
};
